const CUSTOM_COLLECTION_META_KEYS = [
  'also_show',
  'also_param',
  'also_heading',
  'fallback_param',
  'fallback_value',
  'fallback_heading',
  'section_id',
];

// Swaps each card's Liquid price for a sale price when an automatic discount
// takes the variant below its regular price. Compare-at sales are already
// rendered by the price snippet, so cards with no automatic discount are left
// alone. Prices stay hidden until this settles (see the stylesheet).
function applyAutomaticDiscounts(root = document) {
  const priceElements = Array.from(
    root.querySelectorAll('[data-discount-variant-id]:not([data-discount-checked])')
  );

  const reveal = () => priceElements.forEach(element => element.setAttribute('data-discount-ready', ''));

  if (!priceElements.length) {
    return;
  }

  if (!window.storefrontDiscounts) {
    reveal();

    return;
  }

  priceElements.forEach(element => element.setAttribute('data-discount-checked', ''));

  window.storefrontDiscounts
    .requestDiscountedPrices(priceElements.map(element => element.dataset.discountVariantId))
    .then(discountedPrices => {
      priceElements.forEach(element => {
        const price = Number(element.dataset.price);
        const discountedPrice = discountedPrices.get(Number(element.dataset.discountVariantId));

        if (discountedPrice === null || discountedPrice === undefined || discountedPrice >= price) {
          return;
        }

        renderSalePrice(element, Math.max(price, Number(element.dataset.compareAtPrice) || 0), discountedPrice);
      });
    })
    .finally(reveal);
}

// Mirrors the sale markup of the price snippet.
function renderSalePrice(element, regularPrice, salePrice) {
  const labels = element.closest('.custom-collection')?.dataset || {};
  const format = amount => formatPrice(amount, window.theme.settings.money_format);

  const create = (tag, className, text) => {
    const node = document.createElement(tag);

    if (className) {
      node.className = className;
    }

    if (text !== undefined) {
      node.textContent = text;
    }

    return node;
  };

  const price = create('div', 'price price--on-sale');
  const container = create('div', 'price__container');
  const sale = create('div', 'price__sale');
  const saleInner = create('div', 'price__sale-inner');
  const ins = create('ins');

  ins.append(create('span', 'visually-hidden', labels.labelPriceSale), create('span', '', format(salePrice)));
  saleInner.append(
    create('span', 'visually-hidden', labels.labelPriceRegular),
    create('s', '', format(regularPrice)),
    ins
  );
  sale.append(saleInner);
  container.append(sale);
  price.append(create('span', 'visually-hidden', format(salePrice)), container);

  element.replaceChildren(price);
}

applyAutomaticDiscounts();

function buildAlternateGridRequest({
  currentParams,
  sectionId,
  replaceParam,
  replaceValue,
  heading,
  skipKeys = [],
  clearOtherFilters = false,
}) {
  if (!replaceParam || !replaceValue || !sectionId) {
    return null;
  }

  const filterKey = replaceParam.startsWith('filter.') ? replaceParam : `filter.p.m.${replaceParam}`;
  const fetchParams = new URLSearchParams();

  fetchParams.set('section_id', sectionId);

  if (!clearOtherFilters) {
    currentParams.forEach((value, key) => {
      if (
        CUSTOM_COLLECTION_META_KEYS.includes(key) ||
        skipKeys.includes(key) ||
        key === filterKey
      ) {
        return;
      }

      fetchParams.append(key, value);
    });
  }

  fetchParams.append(filterKey, replaceValue);

  return {
    url: `${window.location.pathname}?${fetchParams.toString()}`,
    heading: heading || replaceValue,
  };
}

function populateAlternateGrid(element, request, {
  headingSelector,
  gridSelector,
  onSuccess,
}) {
  const heading = element.querySelector(headingSelector);
  const gridTarget = element.querySelector(gridSelector);

  if (!gridTarget) {
    return;
  }

  if (heading) {
    heading.textContent = request.heading;
  }

  fetch(request.url)
    .then((response) => {
      if (!response.ok) {
        throw new Error('Alternate grid request failed');
      }

      return response.text();
    })
    .then((html) => {
      const grid = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector('.custom-collection__grid');

      if (!grid || !grid.children.length) {
        return;
      }

      gridTarget.className = grid.className;
      gridTarget.innerHTML = grid.innerHTML;
      element.removeAttribute('hidden');
      applyAutomaticDiscounts(gridTarget);

      if (typeof onSuccess === 'function') {
        onSuccess();
      }
    })
    .catch(() => {});
}

if (!customElements.get('custom-collection-also')) {
  class CustomCollectionAlso extends HTMLElement {
    connectedCallback() {
      if (this.dataset.loaded === 'true') {
        return;
      }

      this.dataset.loaded = 'true';
      this.loadAlsoGrid();
    }

    loadAlsoGrid() {
      const currentParams = new URLSearchParams(window.location.search);
      const request = buildAlternateGridRequest({
        currentParams,
        sectionId: this.dataset.sectionId,
        replaceParam: currentParams.get('also_param')?.trim(),
        replaceValue: currentParams.get('also_show')?.trim(),
        heading: currentParams.get('also_heading')?.trim(),
      });

      if (!request) {
        return;
      }

      populateAlternateGrid(this, request, {
        headingSelector: '[data-also-heading]',
        gridSelector: '[data-also-grid]',
      });
    }
  }

  customElements.define('custom-collection-also', CustomCollectionAlso);
}

if (!customElements.get('custom-collection-fallback')) {
  class CustomCollectionFallback extends HTMLElement {
    connectedCallback() {
      if (this.dataset.loaded === 'true') {
        return;
      }

      this.dataset.loaded = 'true';
      this.loadFallbackGrid();
    }

    loadFallbackGrid() {
      const currentParams = new URLSearchParams(window.location.search);
      const request = buildAlternateGridRequest({
        currentParams,
        sectionId: this.dataset.sectionId,
        replaceParam: currentParams.get('fallback_param')?.trim(),
        replaceValue: currentParams.get('fallback_value')?.trim(),
        heading: currentParams.get('fallback_heading')?.trim(),
        clearOtherFilters: true,
      });

      if (!request) {
        return;
      }

      populateAlternateGrid(this, request, {
        headingSelector: '[data-fallback-heading]',
        gridSelector: '[data-fallback-grid]',
        onSuccess: () => {
          const emptyMessage = this.closest('.custom-collection')
            ?.querySelector('[data-custom-collection-empty]');

          if (emptyMessage) {
            emptyMessage.hidden = true;
          }
        },
      });
    }
  }

  customElements.define('custom-collection-fallback', CustomCollectionFallback);
}
